/**
 * Catalog photo AI pipeline (Claude analyze + GPT Image) via Management API.
 * Keys live on the server (ANTHROPIC_API_KEY, OPENAI_API_KEY).
 */

import { manageAPI } from "./index.js";

function extractErrorMessage(e, fallback) {
  const d = e.response?.data;
  return (
    (typeof d?.error === "string" && d.error) ||
    d?.message ||
    e.message ||
    fallback
  );
}

/**
 * GET status — whether the manage-api deployment has both productshot keys configured.
 * @returns {Promise<{ success: boolean, configured: boolean }>}
 */
export async function getProductshotStatus() {
  const { data } = await manageAPI.get("merchant/admin/productshot/status");
  return data;
}

/**
 * POST multipart: image file — Claude analysis JSON (no image generation).
 * @param {File} file
 * @param {{ available_categories?: string[] }} opts
 */
export async function runProductshotAnalyze(file, opts = {}) {
  const { available_categories = [] } = opts;
  const form = new FormData();
  form.append("image", file);
  if (Array.isArray(available_categories) && available_categories.length > 0) {
    form.append(
      "available_categories",
      JSON.stringify(available_categories.map((v) => String(v || "").trim()).filter(Boolean))
    );
  }
  try {
    const { data } = await manageAPI.post("merchant/admin/productshot/analyze", form, {
      timeout: 180000,
    });
    if (!data?.success) {
      throw new Error(data?.error || data?.message || "Analyze did not succeed");
    }
    return data;
  } catch (e) {
    throw new Error(extractErrorMessage(e, "Analyze failed"));
  }
}

/**
 * POST JSON — OpenAI catalog image from generation_prompt.
 * @param {{ generation_prompt: string, quality?: string, size?: string }} body
 */
/**
 * POST JSON — enrich barcode lookup with tax code, default category, and categories.
 * @param {{
 *   product?: object,
 *   available_categories?: { id: string, name: string }[]
 * }} body
 */
export async function enrichBarcodeLookup(body = {}) {
  const product = body.product && typeof body.product === "object" ? body.product : {};
  const available_categories = Array.isArray(body.available_categories)
    ? body.available_categories
        .map((c) => ({
          id: String(c?.id || "").trim(),
          name: String(c?.name || "").trim(),
        }))
        .filter((c) => c.id && c.name)
    : [];
  try {
    const { data } = await manageAPI.post(
      "merchant/admin/productshot/enrich-lookup",
      { product, available_categories },
      { timeout: 120000 }
    );
    if (!data?.success) {
      throw new Error(data?.error || data?.message || "Enrich lookup did not succeed");
    }
    return data;
  } catch (e) {
    throw new Error(extractErrorMessage(e, "Enrich lookup failed"));
  }
}

export async function runProductshotGenerate(body) {
  try {
    const { data } = await manageAPI.post("merchant/admin/productshot/generate", body, {
      timeout: 180000,
    });
    if (!data?.success) {
      throw new Error(data?.error || data?.message || "Generate did not succeed");
    }
    return data;
  } catch (e) {
    throw new Error(extractErrorMessage(e, "Image generation failed"));
  }
}

/**
 * POST multipart: image file, optional quality/size — full pipeline (legacy / one-shot).
 * @param {File} file
 * @param {{ quality?: string, size?: string }} [opts]
 */
export async function runProductshotPipeline(file, opts = {}) {
  const { quality = "medium", size = "1024x1024" } = opts;
  const form = new FormData();
  form.append("image", file);
  form.append("quality", quality);
  form.append("size", size);
  try {
    const { data } = await manageAPI.post("merchant/admin/productshot/pipeline", form, {
      timeout: 180000,
    });
    if (!data?.success) {
      throw new Error(data?.error || data?.message || "Pipeline did not succeed");
    }
    return data;
  } catch (e) {
    throw new Error(extractErrorMessage(e, "AI pipeline failed"));
  }
}

export function b64ToBlob(b64, mime = "image/png") {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mime });
}
